/// <reference types="vite/client" />

import { acceptHMRUpdate, defineStore } from 'pinia'
import {
  LocationQuery,
  RouteLocationNormalized,
  RouteParamsGeneric,
  RouteRecordNameGeneric,
  RouteMeta,
} from 'vue-router'

import { ApiErrorItem } from '@/types'

type State = {
  previousRoute: null | {
    query: LocationQuery
    params: RouteParamsGeneric
    name: RouteRecordNameGeneric
    hash: string
    meta: RouteMeta
    fullPath: string
  }
  isInitialized: boolean
  isFullInitialized: boolean
  isContentfulInitialized: boolean
  isLoaderVisible: boolean
  newsletterMail: string
  isFooterQuoteVisible: boolean
  isEmergencyPopupVisible: boolean
  criticalViewData: null | {
    templateName: string
    data: string
  }
  notAuthorizedErrors: ApiErrorItem[]
  headerSearchQuery: string
  isContentfulPreview: boolean
}
type Actions = {
  setPreviousRoute: (route: RouteLocationNormalized) => void
  setInitialized: (value: boolean) => void
  setContentfulInitialized: (value: boolean) => void
  setNewsletterMail: (value: string) => void
  toggleLoader: (value: boolean) => void
  toggleFooterQuote: (isFooterQuoteVisible: boolean) => void
  toggleEmergencyPopup: (isEmergencyPopupVisible: boolean) => void
  setCriticalViewData: (data: { templateName: string; data: string }) => void
  setNotAuthorizedErrors: (errors: ApiErrorItem[]) => void
  setHeaderSearchQuery: (query: string) => void
  setIsContentfulPreview: (isContentfulPreview: boolean) => void
}
export const useAppStore = defineStore<string, State, Record<string, never>, Actions>('app', {
  state: () => ({
    previousRoute: null,
    isInitialized: false,
    isFullInitialized: false,
    isContentfulInitialized: false,
    isLoaderVisible: false,
    newsletterMail: '',
    isFooterQuoteVisible: true,
    isEmergencyPopupVisible: true,
    criticalViewData: null,
    notAuthorizedErrors: [],
    headerSearchQuery: '',
    isContentfulPreview: false,
  }),

  actions: {
    setPreviousRoute(route) {
      this.previousRoute = {
        query: route.query,
        params: route.params,
        name: route.name,
        hash: route.hash,
        meta: route.meta,
        fullPath: route.fullPath,
      }
    },

    setInitialized(value) {
      this.isInitialized = value
    },

    setContentfulInitialized(value) {
      this.isContentfulInitialized = value
    },

    setNewsletterMail(value) {
      this.newsletterMail = value
    },

    toggleLoader(value) {
      this.isLoaderVisible = value
    },

    toggleFooterQuote(isFooterQuoteVisible) {
      this.isFooterQuoteVisible = isFooterQuoteVisible
    },

    toggleEmergencyPopup(isEmergencyPopupVisible) {
      this.isEmergencyPopupVisible = isEmergencyPopupVisible
    },

    setCriticalViewData(data) {
      this.criticalViewData = data
    },

    setNotAuthorizedErrors(errors) {
      this.notAuthorizedErrors = errors
    },

    setHeaderSearchQuery(query) {
      this.headerSearchQuery = query
    },

    setIsContentfulPreview(isContentfulPreview) {
      this.isContentfulPreview = isContentfulPreview
    },
  },

  persist: {
    pick: ['isFooterQuoteVisible', 'isEmergencyPopupVisible'],
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAppStore, import.meta.hot))
}
